import { FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import React, { useEffect } from 'react'
import {
  ArrayInput,
  Loading,
  ReferenceInput,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useArrayInput,
  useLocale,
  useSourceContext,
  useTranslate,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { makeStyles } from 'tss-react/mui'
import {
  AutocompleteTextInput,
  CompactRemoveButton,
  DynamicInput,
  FormDataConsumer,
} from '~/components'
import { Categories, Sections } from '~/graphql/generated'
import { notesIconChoices } from '~/types/choices'
import { defaultCategory } from './equipmentDefaults'
import './UpsertBoatAttributes.css'

const useStyles = makeStyles()((theme) => ({
  disguisedInput: {
    [`& .MuiInputBase-root`]: {
      background: 'none',
    },
    [`& .MuiFilledInput-underline:before`]: {
      border: 'none',
    },
    [`& .MuiFormLabel-root.Mui-disabled`]: {
      color: theme.palette.text.primary,
    },
    [`& .MuiInputBase-root.Mui-disabled`]: {
      color: theme.palette.text.primary,
    },
    [`& .MuiInputBase-input.Mui-disabled::placeholder`]: {
      color: theme.palette.text.primary,
    },
  },
}))

/**
 * The full form path of a source given relative to the current row.
 *
 * react-admin 5 scopes `source` to the enclosing <ArrayInput> row, which is
 * what inputs want — but react-hook-form's getValues/setValue take full paths.
 * Used relative there, they read and wrote the top of the form, so a new
 * section never got its categories, nor a new category its attributes.
 */
const useFullSource = (source: string) => useSourceContext().getSource(source)

const AttributesFromCategory = ({
  category,
  formData,
  source,
  guided,
  ...rest
}: {
  category: Categories
  formData: any
  source: string
  guided?: boolean
  /** Forwarded to the rendered input. */
  [key: string]: any
}) => {
  const { getValues, setValue } = useFormContext()
  const { attributes } = category
  const path = useFullSource(source)

  // A category just added to the boat starts with its visible attributes, at
  // their default values.
  useEffect(() => {
    if (getValues(path) === undefined) {
      setValue(
        path,
        attributes
          .filter((a) => !a.hidden)
          .map((a) => ({ attribute_id: a.id, value: a.default_value })),
        { shouldDirty: true }
      )
    }
  }, [path, attributes, getValues, setValue])

  const handleAttributeSelected = (event) => {
    const attribute_id = event.target.value
    const attribute = attributes.find((a) => a.id === attribute_id)
    if (!attribute) return
    setValue(
      [...event.target.name.split('.').slice(0, -1), 'value'].join('.'),
      attribute.default_value
    )
  }

  return (
    <ArrayInput {...rest} source={source} label='' className='boat-attributes'>
      <SimpleFormIterator
        removeButton={<CompactRemoveButton />}
        disableReordering
        disableRemove={guided}
        disableAdd={guided}
        getItemLabel={() => ''}
      >
        {!guided && (
          <ReferenceInput
            source='attribute_id'
            reference='attributes'
            filter={{ category_id: category.id }}
            onChange={handleAttributeSelected}
          >
            {/* The label on the input: react-admin 5 no longer passes
                ReferenceInput's down, which showed "Attribute". */}
            <SelectInput optionText='name' label='resources.attributes.fields.name' />
          </ReferenceInput>
        )}
        <FormDataConsumer>
          {({ scopedFormData: { attribute_id } = {}, getSource, ...rest }) => {
            const attribute = attributes.find((a) => a.id === attribute_id)
            const valueSource = 'value'

            return attribute ? (
              <DynamicInput attribute={attribute} source={valueSource} standalone={guided} />
            ) : null
          }}
        </FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  )
}

/**
 * "Ausstattung hinzufügen": rendered inside the category <ArrayInput> and adds
 * through its append(), with the category's attributes at their defaults.
 */
const AddBoatCategorySelection = ({ categories }: { categories: Categories[] }) => {
  const translate = useTranslate()
  const { append } = useArrayInput()

  return (
    <FormControl variant='outlined' margin='dense' sx={{ minWidth: 280 }}>
      <InputLabel id='category-label'>{translate('resources.categories.add')}</InputLabel>
      <Select
        labelId='category-label'
        label={translate('resources.categories.add')}
        value=''
        onChange={(e) => {
          const category = categories.find((c) => c.id === Number(e.target.value))
          if (category) append(defaultCategory(category))
        }}
      >
        {Object.values(categories).map((category) => (
          <MenuItem key={category.id} value={category.id}>
            {category.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const UpsertBoatSection = ({
  section,
  formData,
  source,
  guided,
  showNotes,
  ...rest
}: UpsertBoatSectionProps) => {
  const { getValues, setValue } = useFormContext()
  const { categories } = section
  const locale = useLocale()
  const { classes } = useStyles()
  const path = useFullSource(source)

  // A section just added to the boat starts with all of its categories.
  useEffect(() => {
    if (getValues(path) === undefined) {
      setValue(
        path,
        categories.map((c) => ({ category_id: c.id })),
        { shouldDirty: true }
      )
    }
  }, [path, categories, getValues, setValue])

  const handleCategorySelected = (event) => {
    const category_id = event.target.value
    const category = categories.find((c) => c.id === category_id)
    // setValue(
    //   [...event.target.name.split('.').slice(0, -1), 'value'].join('.'),
    //   category.default_value
    // )
  }

  const handleMultilineKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.stopPropagation()
    }
  }

  return (
    <div>
      <ArrayInput source={source} label='' className='boat-categories'>
        <SimpleFormIterator
          disableAdd
          disableReordering
          {...(showNotes ? { removeButton: <CompactRemoveButton /> } : {})}
        >
          <FormDataConsumer>
            {({ scopedFormData: { category_id } = {}, getSource, ...rest }) => {
              if (!category_id) {
                return null
              }

              //const { data, loading } = useGetOne('categories', category_id)
              const data = categories.find((c) => c.id === category_id)
              const loading = false

              // The category may not be in the loaded page.
              if (!data) return null

              const nameSource = 'name'

              return loading ? (
                <Loading />
              ) : (
                <Grid container spacing={1}>
                  {guided ? (
                    <Grid size={{ md: 4 }}>
                      {/* <Typography component="span" variant="body1">
                        {data?.translated_name?.[locale] || data?.name}
                      </Typography> */}
                      <TextInput
                        source={nameSource}
                        label={data?.translated_name?.[locale] || data?.name}
                        className={classes.disguisedInput}
                        disabled
                      />
                    </Grid>
                  ) : (
                    <Grid size={{ md: 4 }}>
                      <AutocompleteTextInput
                        source={nameSource}
                        label={data?.translated_name?.[locale] || data?.name}
                        where={{ category_id: category_id }}
                      />
                    </Grid>
                  )}
                  {guided ? (
                    <Grid size={{ md: 5 }}>
                      <TextInput
                        source={'description'}
                        placeholder={data.translated_description?.[locale] || data.description}
                        slotProps={{ inputLabel: { shrink: true } }}
                        label={'resources.categories.fields.description'}
                        className={classes.disguisedInput}
                        disabled
                      />
                    </Grid>
                  ) : (
                    <Grid size={{ md: 5 }}>
                      <TextInput
                        source={'description'}
                        placeholder={data.translated_description?.[locale] || data.description}
                        slotProps={{ inputLabel: data.description ? { shrink: true } : {} }}
                        label={'resources.categories.fields.description'}
                      />
                    </Grid>
                  )}
                  <Grid size={{ md: 3 }}>
                    <SelectInput
                      style={{ width: 'auto', float: 'right' }}
                      source={'notes_icon'}
                      label='resources.categories.notes_icon'
                      choices={notesIconChoices}
                      translateChoice={false}
                      optionText={(value) => React.createElement(value.icon, { fontSize: 'small' })}
                    />
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid size={{ lg: showNotes ? 8 : 12 }}>
                      <AttributesFromCategory
                        category={data}
                        source={'boat_section_attribute'}
                        label='resources.attributes.title'
                        guided={guided}
                        {...rest}
                      />
                    </Grid>
                    {showNotes && (
                      <Grid size={{ lg: 4 }}>
                        <TextInput
                          source={'notes'}
                          label='resources.categories.notes'
                          multiline
                          minRows={4}
                          onKeyPress={handleMultilineKeyPress}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )
            }}
          </FormDataConsumer>
        </SimpleFormIterator>
        <AddBoatCategorySelection categories={categories} />
      </ArrayInput>
    </div>
  )
}

export interface UpsertBoatSectionProps {
  section: Sections
  formData: any
  source: string
  guided: boolean
  showNotes: boolean
}

export default UpsertBoatSection
